import React from 'react';
import { Link } from 'react-router-dom';
import { SidebarSearchModal } from '@backstage/plugin-search';
import Logo from '../Logo';
import tabs from './tabs';
import NavigationItem from './NavigationItem';
import {
  AppBar,
  Toolbar,
  Divider,
  styledComponent,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import Settings from './Settings';

/**
 * Custom styled component for the search bar in the top navigation.
 * Provides responsive styling and hover effects using theme properties.
 */
const Search = styledComponent('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '11px',
  backgroundColor: theme.palette.surfaceContainer.high,
  '&:hover': {
    backgroundColor: theme.palette.surface.outlineVariant,
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
  '& svg': {
    color: theme.palette.primary.main,
    display: 'flex',
  },
  '& button': {
    height: 'auto',
    minWidth: 'auto',
    width: 'auto',
    padding: '5px',
    borderRadius: '11px',
    '&:hover': {
      backgroundColor: theme.palette.surface.outlineVariant,
    },
  },
}));

/**
 * Renders the top navigation bar for the application.
 *
 * The navigation bar includes:
 * - A logo that links to the home page.
 * - Navigation tabs dynamically generated from a configuration file.
 * - A vertical divider separating the navigation section.
 * - A search modal for sidebar search functionality.
 * - A settings menu for user preferences.
 *
 * @returns A React element representing the top navigation bar.
 *
 * @category Navigation
 */
export const TopNavigation = (): React.ReactElement => {
  return (
    <AppBar position="static" sx={{ background: 'transparent' }} elevation={0}>
      <Toolbar>
        {/* Logo linking to the home page */}
        <Link
          to="/"
          aria-label="Home"
          style={{
            marginLeft: 20,
            display: 'inline-flex',
            alignSelf: 'middle',
          }}
        >
          <Logo />
        </Link>

        {/* Dynamically render navigation items based on the tabs configuration */}
        {tabs.map(NavigationItem)}

        {/* Vertical divider between navigation items and the search bar */}
        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          sx={{ margin: '17px' }}
        />

        {/* Search bar with a sidebar search modal */}
        <Search>
          <SidebarSearchModal />
        </Search>

        {/* Settings menu for user preferences */}
        <Settings />
      </Toolbar>
    </AppBar>
  );
};
